<template>
  <a-popup :visible="visible" @cancel="onCancel">
    <div
      class="tw-mx-auto tw-w-[95%] md:tw-max-w-[700px] tw-relative tw-h-fit tw-overflow-auto tw-flex tw-flex-col tw-border tw-border-solid tw-border-gray-200 tw-shadow-lg tw-my-10 tw-rounded-lg">
      <div class="tw-relative tw-bg-white tw-rounded-lg">
        <div class="tw-p-4 tw-flex tw-items-center tw-justify-between tw-border-b tw-border-gray-100">
          <div class="tw-flex tw-items-center tw-gap-3">
            <div class="tw-bg-primary-50 tw-p-2 tw-rounded-md">
              <icon icon="solar:settings-bold-duotone" class="tw-text-xl tw-text-primary-500" />
            </div>
            <h3 class="tw-text-lg tw-font-semibold tw-text-gray-800">{{ $t('common.column_visibility') }}</h3>
          </div>
          
          <button @click="onCancel"
            class="tw-p-1.5 tw-bg-gray-50 tw-rounded-full tw-border tw-border-solid tw-border-gray-200 hover:tw-bg-gray-100 tw-duration-200 tw-flex tw-items-center tw-justify-center">
            <icon icon="solar:close-circle-bold-duotone" class="tw-text-lg tw-text-gray-500" />
          </button>
        </div>

        <div class="tw-p-5">
          <div class="tw-flex tw-items-center tw-justify-between tw-mb-4">
            <p class="tw-text-sm tw-text-gray-500">{{ $t('messages.choose_visible_columns') }}</p>
            <div class="tw-text-xs tw-text-gray-500">
              <span class="tw-inline-flex tw-items-center tw-bg-gray-100 tw-px-2 tw-py-1 tw-rounded">
                <icon icon="solar:sort-by-time-linear" class="tw-w-4 tw-h-4 tw-mr-1" />
                {{ $t('common.drag_to_reorder') }}
              </span>
            </div>
          </div>
          
          <div class="tw-bg-gray-50 tw-p-5 tw-rounded-lg tw-border tw-border-solid tw-border-gray-200">
            <draggable 
              v-model="orderedColumns" 
              item-key="name"
              @start="drag=true" 
              @end="drag=false"
              :animation="200"
              ghost-class="tw-bg-gray-100"
              handle=".drag-handle"
              :disabled="!reorderEnabled"
              class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 md:tw-grid-cols-2 tw-gap-3"
            >
              <template #item="{element}">
                <div 
                  class="tw-flex tw-items-center tw-p-2 tw-bg-white tw-rounded-md tw-border tw-border-gray-100 hover:tw-border-primary-200 hover:tw-bg-primary-50/30 tw-duration-200 tw-group"
                  :class="{ 
                    'tw-opacity-60 tw-cursor-not-allowed': element.name === 'actions', 
                    'tw-cursor-pointer': element.name !== 'actions',
                    'tw-border-primary-300 tw-bg-primary-50/50': selectedColumns[element.name]
                  }"
                >
                  <div class="tw-flex tw-items-center tw-grow tw-mr-2" @click="element.name !== 'actions' && toggleColumn(element.name)">
                    <div
                      class="tw-min-w-[20px] tw-min-h-[20px] tw-w-5 tw-h-5 tw-bg-white tw-rounded tw-border tw-border-gray-300 tw-flex tw-items-center tw-justify-center tw-transition tw-duration-200"
                      :class="[selectedColumns[element.name] ? '!tw-bg-primary-500 !tw-border-primary-500' : '']">
                      <icon v-if="selectedColumns[element.name]" icon="mdi:check" class="tw-w-4 tw-h-4 tw-text-white" />
                    </div>
                    <span class="tw-ms-2 tw-text-sm tw-font-medium" 
                      :class="selectedColumns[element.name] ? 'tw-text-gray-800' : 'tw-text-gray-600'">
                      {{ $t(element.label) }}
                    </span>
                  </div>
                  
                  <div 
                    v-if="element.name !== 'actions'" 
                    class="drag-handle tw-p-1 tw-text-gray-400 tw-opacity-0 group-hover:tw-opacity-100 tw-duration-200 tw-cursor-grab active:tw-cursor-grabbing"
                    :class="{ '!tw-opacity-0 !tw-cursor-default': !reorderEnabled }"
                  >
                    <icon icon="solar:square-sort-vertical-broken" class="tw-w-5 tw-h-5" />
                  </div>
                  <div v-else class="tw-w-7"></div>
                </div>
              </template>
            </draggable>
          </div>
        </div>

        <div class="tw-bg-gray-50 tw-p-4 tw-flex tw-items-center tw-gap-2 tw-justify-between tw-rounded-b-lg tw-border-t tw-border-gray-100">
          <div>
            <label class="tw-flex tw-items-center tw-cursor-pointer">
              <input type="checkbox" v-model="reorderEnabled" class="tw-sr-only" />
              <div class="tw-w-9 tw-h-5 tw-bg-gray-300 tw-rounded-full tw-relative tw-transition-colors tw-duration-200" :class="{ '!tw-bg-primary-500': reorderEnabled }">
                <div class="tw-w-4 tw-h-4 tw-bg-white tw-rounded-full tw-absolute tw-left-0.5 tw-top-0.5 tw-transform tw-transition-transform tw-duration-200" :class="{ 'tw-translate-x-4': reorderEnabled }"></div>
              </div>
              <span class="tw-ms-2 tw-text-xs tw-font-medium tw-text-gray-700">{{ $t('common.enable_reordering') }}</span>
            </label>
          </div>
          <div class="tw-flex tw-items-center tw-gap-2">
            <a-button intent="simple" @click="onCancel" class="tw-h-9">{{ $t('buttons.cancel') }}</a-button>
            <a-button intent="secondary" @click="resetToDefault" class="tw-h-9">{{ $t('buttons.reset_defaults') }}</a-button>
            <a-button intent="primary" @click="saveChanges" class="tw-h-9">{{ $t('buttons.save') }}</a-button>
          </div>
        </div>
      </div>
    </div>
  </a-popup>
</template>

<script setup>
import { defineProps, defineEmits, watch, toRef, reactive, ref, onBeforeUnmount } from 'vue';
import { saveInLocalStorage, getFromLocalStorage } from '@/utils/local-storage';
import draggable from 'vuedraggable';

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  columns: {
    type: Array,
    required: true
  },
  storageKey: {
    type: String,
    default: 'order_table_columns'
  }
});

const emit = defineEmits(['update:visible', 'update:columns']);
const visible = toRef(props, 'visible');
const columns = toRef(props, 'columns');

// Enable/disable reordering
const reorderEnabled = ref(false);
const drag = ref(false);

// Initialize selected columns from localStorage or default to all visible
const selectedColumns = reactive({});

// Track column order
const orderedColumns = ref([]);

// Initialize selected columns when component is created
const initSelectedColumns = () => {
  const savedData = getFromLocalStorage(props.storageKey, null);
  
  // Default values if no saved data
  let savedColumns = null;
  let savedOrder = null;
  
  if (savedData && typeof savedData === 'object') {
    if (savedData.columns) {
      savedColumns = savedData.columns;
    }
    if (savedData.order) {
      savedOrder = savedData.order;
    }
  } else if (savedData) {
    // For backward compatibility with older format
    savedColumns = savedData;
  }
  
  // Default columns that should be visible
  const defaultVisibleColumns = [
    'id', 'customer_name', 'customer_phone', 'customer_city', 
    'order_summary', 'price', 'calls', 'agent_status', 'followup_status', 
    'delivery_id', 'delivery_status', 'delivery_time', 'actions'
  ];
  
  // Initialize selected columns
  columns.value.forEach(column => {
    if (savedColumns !== null) {
      // If we have saved preferences, use them
      selectedColumns[column.name] = savedColumns[column.name] !== undefined 
        ? savedColumns[column.name] 
        : defaultVisibleColumns.includes(column.name);
    } else {
      // Default: show only default columns
      selectedColumns[column.name] = defaultVisibleColumns.includes(column.name);
    }
  });
  
  // Always show actions column
  if (columns.value.some(col => col.name === 'actions')) {
    selectedColumns['actions'] = true;
  }
  
  // Initialize column order
  if (savedOrder) {
    // Get columns in the saved order
    const orderedList = [];
    
    // First add columns that exist in the saved order
    savedOrder.forEach(colName => {
      const found = columns.value.find(col => col.name === colName);
      if (found) {
        orderedList.push(found);
      }
    });
    
    // Then add any new columns that might not be in the saved order
    columns.value.forEach(col => {
      if (!orderedList.some(orderedCol => orderedCol.name === col.name)) {
        orderedList.push(col);
      }
    });
    
    orderedColumns.value = orderedList;
  } else {
    // Default: use the original column order
    orderedColumns.value = [...columns.value];
  }
};

watch(visible, (newValue) => {
  if (newValue) {
    initSelectedColumns();
    reorderEnabled.value = false;
  }
});

const toggleColumn = (columnName) => {
  if (columnName !== 'actions') {
    selectedColumns[columnName] = !selectedColumns[columnName];
  }
};

const onCancel = () => {
  emit('update:visible', false);
};

const saveChanges = () => {
  // Get the current column order
  const columnOrder = orderedColumns.value.map(col => col.name);
  
  // Save both visibility and order to localStorage
  saveInLocalStorage(props.storageKey, {
    columns: selectedColumns,
    order: columnOrder
  });
  
  // Emit updated columns and order to parent
  emit('update:columns', {
    visibility: selectedColumns,
    order: columnOrder
  });
  
  // Close popup
  onCancel();
};

const resetToDefault = () => {
  // Default columns that should be visible
  const defaultVisibleColumns = [
    'id', 'customer_name', 'customer_phone', 'customer_city', 
    'order_summary', 'price', 'calls', 'agent_status', 'followup_status', 
    'delivery_id', 'delivery_status', 'delivery_time', 'actions'
  ];
  
  // Reset columns to default visibility
  columns.value.forEach(column => {
    selectedColumns[column.name] = defaultVisibleColumns.includes(column.name);
  });
  
  // Always show actions column
  if (columns.value.some(col => col.name === 'actions')) {
    selectedColumns['actions'] = true;
  }
  
  // Reset column order to original
  orderedColumns.value = [...columns.value];
  
  // Save changes
  saveChanges();
};

// When component is destroyed, save the current state
onBeforeUnmount(() => {
  const savedData = {
    columns: selectedColumns,
    order: orderedColumns.value.map(col => col.name)
  };
  saveInLocalStorage(props.storageKey, savedData);
});

// Initialize on component creation
watch(columns, () => {
  initSelectedColumns();
}, { immediate: true });
</script>

<style>
.sortable-ghost {
  opacity: 0.5;
  background: #f3f4f6 !important;
}

.sortable-chosen {
  background: #f0f9ff !important;
}
</style> 